/*
 * From https://www.redblobgames.com/x/2117-vehicle-movement-on-grid/
 * Copyright 2021 Red Blob Games <redblobgames@gmail.com>
 * @license Apache-2.0 <https://www.apache.org/licenses/LICENSE-2.0.html>
 */

///<reference path="/Users/amitp/Sites/redblobgames/x/1740-map-painter/node_modules/vue/types/vue.d.ts"/>

const TILE_SIZE = 20;

const TRUCK_SHAPE = {
    front: 1/2,
    back: -1,
    frontWheel: 1/4,
    backWheel: -3/4,
}

type GridPosition = {q: number, r: number};
type GridEdge = {q: number, r: number, s: 'N'|'E'|'S'|'W'};
enum Turtle { Forward, Left, Right };
type TurtleRoute = {start: GridEdge, commands: Turtle[]};
type RouteData = {
    command: Turtle,
    startEdge: GridEdge,
    endEdge: GridEdge,
    tile: GridPosition,
    length: number,
    startPosition: number,
    endPosition: number,
};

type Segment =
    {type: 'line', x1: number, y1: number, x2: number, y2: number, length: 1}
    | {type: 'arc', cx: number, cy: number, r: number, length: number};


function clamp(x: number, lo: number, hi: number): number { return x < lo ? lo : x > hi ? hi : x; }
function lerp(a: number, b: number, t: number): number { return a * (1-t) + b * t; }
function mod(a: number, b: number): number { return (a % b + b) % b; }
function radiansToDegrees(radians: number): number { return 180 / Math.PI * radians; }

function midpointOfEdge({q, r, s}: GridEdge): GridPosition {
    q += 0.5;
    r += 0.5;
    switch (s) {
        case 'N': r -= 0.5; break;
        case 'E': q += 0.5; break;
        case 'S': r += 0.5; break;
        case 'W': q -= 0.5; break;
    }
    return {q, r};
}

Vue.component('a-wheels', {
    props: {q: Number, r: Number},
    template: `<path class="wheels" :transform="transform"
                     d="M -2,4 l 4,0 M -2,16 l 4,0"/>`,
    computed: {
        transform() { return `translate(${this.q * TILE_SIZE}, ${this.r * TILE_SIZE})`; },
    },
});

Vue.component('a-road', {
    props: {d: String},
    template: `<g>
          <path class="road-0" :d="d"/>
          <path class="road-1" :d="d" />
          <path class="road-2" :d="d" />
          <path class="road-3" :d="d" />
    </g>`,
});

Vue.component('a-truck', {
    props: ['truck'],
    template: `<g :transform="transform">
      <g transform="translate(-20,-10)">
        <rect class="container" x="0" y="5" width="20" height="10"/>
        <a-wheels :q="0.25" :r="0"/>
        <a-wheels :q="1.25" :r="0"/>
        <use href="#truck-cab" transform="translate(20,0)" />
      </g>
      <circle cx="-20" r="1.5" fill="red" stroke="white" stroke-width="0.5" />
      <circle r="1.5" fill="red" stroke="black" stroke-width="0.5" />
      <circle cx="-15" r="1.5" fill="blue" stroke="white" stroke-width="0.5" />
      <circle cx="5" r="1.5" fill="blue" stroke="black" stroke-width="0.5" />
    </g>`,
    computed: {
        transform() {
            let {position, rotation} = this.truck;
            return `translate(${position.x},${position.y}) rotate(${rotation}) translate(5,0)`;
        },
    },
});



Vue.component('a-track', {
    props: {d: String},
    template: `<g>
          <path class="track-0" :d="d"/>
          <path class="track-1" :d="d" />
          <path class="track-2" :d="d" />
    </g>`,
});

Vue.component('a-traincar', {
    props: {transform: String},
    template: `<g :transform="transform">
                 <path class="coupling" d="M 0,10 l 60,0"/>
                 <a-wheels :q="0.35" :r="0" />
                 <a-wheels :q="2.65" :r="0" />
                 <rect class="vehicle" x="2.5" y="4.5" width="55" height="11"/>
                 <rect class="container" x="10" y="5" width="20" height="10"/>
                 <rect class="container" x="30" y="5" width="20" height="10"/>
              </g>`,
});


function turtleForwards(edge: GridEdge): GridEdge {
    let {q, r, s} = edge;
    switch (s) {
        case 'N': return {q, r: r-1, s};
        case 'E': return {q: q+1, r, s};
        case 'S': return {q, r: r+1, s};
        case 'W': return {q: q-1, r, s};
    }
}

function turtleRight(edge: GridEdge): GridEdge {
    let {q, r, s} = edge;
    switch (s) {
        case 'N': return {q, r, s: 'E'};
        case 'E': return {q, r, s: 'S'};
        case 'S': return {q, r, s: 'W'};
        case 'W': return {q, r, s: 'N'};
    }
}

function turtleLeft(edge: GridEdge): GridEdge {
    let {q, r, s} = edge;
    switch (s) {
        case 'N': return {q, r, s: 'W'};
        case 'E': return {q, r, s: 'N'};
        case 'S': return {q, r, s: 'E'};
        case 'W': return {q, r, s: 'S'};
    }
}

/** Given a turtle path, calculate data for each edge */
function turtlePathToData(route: TurtleRoute): RouteData[] {
    let edge = route.start;
    let position = 0;
    let results = [];
    for (let command of route.commands) {
        let data: RouteData = {command, startEdge: edge, startPosition: position, length: 1};
        edge = turtleForwards(edge);
        if (command === Turtle.Left) { edge = turtleLeft(edge); data.length = Math.PI/4; }
        if (command === Turtle.Right) { edge = turtleRight(edge); data.length = Math.PI/4; }
        position += data.length;
        data.tile = {q: edge.q, r: edge.r};
        data.endEdge = edge;
        data.endPosition = position;
        results.push(data);
    }
    return results;
}

/** Given a turtle path, turn it into a sequence of edges */
function turtlePathToEdges(route: TurtleRoute): GridEdge[] {
    let edge = route.start;
    let results = [edge];
    for (let command of route.commands) {
        edge = turtleForwards(edge);
        if (command === Turtle.Left) edge = turtleLeft(edge);
        if (command === Turtle.Right) edge = turtleRight(edge);
        results.push(edge);
    }
    return results;
}

/** Given a turtle path "F 10 L L F 3 R" turn it into a sequence of turtle moves */
function parseTurtlePath(turtle: string): Turtle[] {
    let results = [];
    let commands = turtle.split(" ");
    while (commands.length > 0) {
        switch (commands.shift()) {
            case 'F': {
                let distance = parseInt(commands.shift());
                while (distance > 0) {
                    results.push(Turtle.Forward);
                    distance--;
                }
                break;
            }
            case 'L': {
                results.push(Turtle.Left);
                break;
            }
            case 'R': {
                results.push(Turtle.Right);
                break;
            }
            default:
                throw "Parse error";
        }
    }
    return results;
}

new Vue({
    el: "figure",
    data: {
        trainPosition: 0,
        trainVelocity: 100,
        path: [
            ['line', 0, 10, 400, 0],
        ],
        roads: [
            {start: {q: 2, r: 2, s: 'E'}, commands: parseTurtlePath("F 8 R F 3 R F 8 R F 3 R")},
            {start: {q: 14, r: 2, s: 'W'}, commands: parseTurtlePath("F 2")},
        ],
        truckPosition: 1,
        truckVelocity: 0,
    },
    mounted() {
        let lastTimestamp = null;
        let truckPositionGoal = 0;
        let trainPositionGoal = -100;

        const animate = (timestamp: number) => {
            requestAnimationFrame(animate);

            const maxTruckVelocity = 4;
            const maxTrainVelocity = 100; // different units :-(
            
            const Δt_ms = timestamp - (lastTimestamp ?? timestamp);
            const Δt = Δt_ms * 1e-3;
            lastTimestamp = timestamp;
            
            if (this.truckPosition > truckPositionGoal && this.truckVelocity == 0.0) {
                // start accelerating again
                truckPositionGoal += 20;
            }
            // TODO: need to decelerate to *end up* at positionGoal instead
            // of decelerating after we pass positionGoal
            let truckAcceleration =
                this.truckPosition > truckPositionGoal ? -0.5
                : this.truckVelocity < maxTruckVelocity ? 0.3
                : 0;
            this.truckVelocity = clamp(this.truckVelocity + truckAcceleration * Δt, 0, maxTruckVelocity);
            this.truckPosition += this.truckVelocity * Δt;

            // Train comes in from left, stops, then starts again
            if (this.trainPosition > 600) {
                this.trainPosition = -300 - 100 * Math.random();
                this.trainVelocity = 100;
                trainPositionGoal = -100;
            }
            if (this.trainPosition > trainPositionGoal && this.trainVelocity == 0.0) {
                trainPositionGoal = 1000;
            }
            let trainAcceleration =
                this.trainPosition > trainPositionGoal ? -30
                : this.trainVelocity < maxTrainVelocity ? 10
                : 0;
            this.trainVelocity = clamp(this.trainVelocity + trainAcceleration * Δt, 0, maxTrainVelocity);
            this.trainPosition += this.trainVelocity * Δt;
        };
        animate(0);
    },
    computed: {
        roadEdges() { return turtlePathToEdges(this.roads[0]); },
        roadData(): RouteData[] { return turtlePathToData(this.roads[0]); },
        truck() {
            let end = this.roadPosition(this.truckPosition),
            backWheel = this.roadPosition(this.truckPosition + TRUCK_SHAPE.backWheel),
            frontWheel = this.roadPosition(this.truckPosition + TRUCK_SHAPE.frontWheel);

            end.q = lerp(backWheel.q, frontWheel.q, 3/4);
            end.r = lerp(backWheel.r, frontWheel.r, 3/4);
            
            return {
                position: {x: end.q * TILE_SIZE, y: end.r * TILE_SIZE},
                rotation: radiansToDegrees(Math.atan2(frontWheel.r - backWheel.r, frontWheel.q - backWheel.q)),
            };
        },
        roadPath() {
            let svgPath = [];
            let startPosition = midpointOfEdge(this.roadData[0].startEdge);
            svgPath.push(`M ${startPosition.q * TILE_SIZE},${startPosition.r * TILE_SIZE}`);
            for (let segment of this.roadData) {
                let {q, r} = midpointOfEdge(segment.endEdge);
                if (segment.command === Turtle.Forward) {
                    svgPath.push(`L ${q * TILE_SIZE},${r * TILE_SIZE}`);
                } else {
                    svgPath.push(`A ${0.5 * TILE_SIZE},${0.5 * TILE_SIZE}
                                    0,0,1 ${q * TILE_SIZE},${r * TILE_SIZE}`);
                }
            }
            return svgPath.join(" ");
        },
    },
    methods: {
        /** calculate the q,r position 'distance' units from start */
        roadPosition(position: number): GridPosition {
            const routeLength = this.roadData[this.roadData.length-1].endPosition;
            position = mod(position, routeLength);
            for (let segment of this.roadData) {
                if (segment.startPosition <= position
                    && position < segment.endPosition) {
                    let p = position - segment.startPosition;
                    switch (segment.command) {
                        case Turtle.Forward: {
                            let a = midpointOfEdge(segment.startEdge),
                                b = midpointOfEdge(segment.endEdge);
                            return {
                                q: lerp(a.q, b.q, p),
                                r: lerp(a.r, b.r, p),
                            };
                        }
                        case Turtle.Right: {
                            let start = midpointOfEdge(segment.startEdge);
                            let n = {q: 0, r: 0};
                            switch (segment.endEdge.s) {
                                case 'N': n.r = -1/2; break;
                                case 'E': n.q = +1/2; break;
                                case 'S': n.r = +1/2; break;
                                case 'W': n.q = -1/2; break;
                            }
                            let center = {q: start.q + n.q, r: start.r + n.r};
                            let rotationFromEnd = p*2;
                            let line = {
                                q: n.q * Math.cos(rotationFromEnd) - n.r * Math.sin(rotationFromEnd),
                                r: n.q * Math.sin(rotationFromEnd) + n.r * Math.cos(rotationFromEnd)
                            };
                            return {q: center.q - line.q, r: center.r - line.r};
                        }
                        case Turtle.Left: {
                            throw `Not implemented`;
                        }
                    }
                }
            }
            throw `Should not happen ${position}`;
        },
        transform(offset) {
            // NOTE: used only for train cars, need to separate out into its own component
            return `translate(${offset+this.trainPosition},10) translate(-30,-10)`;
        },
    },
});
